import { toInteger } from 'lodash';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import {
  GroupType,
  type GroupWithGroupUsers,
  type PostAndPatchGroupFields
} from 'services/assist-api/entities/group/types';
import type { CreateGroupUsersVariables } from 'services/assist-api/entities/groups-users/create';
import { SubmitAction } from './marking-group-form.schema';
import type { GroupMemberFormValue, MarkingGroupFormValues } from './marking-group-form.schema';

function loadGroupMember(
  organisationUser: BaseCustomerUser,
  markingGroup: GroupWithGroupUsers | undefined
): GroupMemberFormValue {
  const groupUser = markingGroup?.users?.find((gu) => gu.user.id === organisationUser.id);
  return {
    groupUserId: groupUser?.id,
    organisationUserId: organisationUser.id,
    checked: groupUser?.id !== undefined
  };
}

export function loadDefaultValues(
  markerOrganisationUsers: BaseCustomerUser[],
  nonMarkerOrganisationUsers: BaseCustomerUser[],
  markingGroup?: GroupWithGroupUsers
): MarkingGroupFormValues {
  return {
    name: markingGroup?.name || '',
    description: markingGroup?.description || '',
    year: markingGroup?.year || new Date().getFullYear().toString(),
    markerGroupMembers: markerOrganisationUsers.map((organisationUser) =>
      loadGroupMember(organisationUser, markingGroup)
    ),
    nonMarkerGroupMembers: nonMarkerOrganisationUsers.map((organisationUser) =>
      loadGroupMember(organisationUser, markingGroup)
    ),
    submitAction: markingGroup ? SubmitAction.Update : SubmitAction.Create
  };
}

/**
 * Determines the group fields to save that are shared between POST and PATCH requests. Does not
 * include group users.
 */
export function markingGroupFieldsToSave(
  formValues: MarkingGroupFormValues
): PostAndPatchGroupFields {
  return {
    name: formValues.name,
    description: formValues.description,
    year: formValues.year,
    type: GroupType.MarkingGroup
  };
}

/**
 * Determines the group users to include via a POST request (either when creating or updating the
 * group).
 */
export function getGroupUsersToPost(
  markerGroupMembers: GroupMemberFormValue[],
  groupId: number
): CreateGroupUsersVariables {
  const organisationUserIds = markerGroupMembers
    .filter((groupMember) => groupMember.groupUserId === undefined && groupMember.checked)
    .map((groupMember) => groupMember.organisationUserId);
  return { customerUsers: organisationUserIds, group: groupId };
}

/**
 * Determines the IDs of the group users to remove via individual DELETE requests. This function is
 * only used when updating a group (group users don't exist until the group is created, so there is
 * nothing to delete when creating).
 */
export function getGroupUserIdsToDelete(
  markerGroupMembers: GroupMemberFormValue[],
  nonMarkerGroupMembers: GroupMemberFormValue[]
): number[] {
  return markerGroupMembers
    .concat(nonMarkerGroupMembers)
    .filter((groupMember) => groupMember.groupUserId !== undefined && !groupMember.checked)
    .map((groupMember) => toInteger(groupMember.groupUserId));
}
